import * as React from "react";
import { withContext, WithContextPlacementProps, K2ComponentState, AcquireControl, StyleHelper } from "./k2hoc";
import { UpdateSplitterPanel, SplitterPanelOrientation, FrgtPanelBaseData } from "../common/communication.base";
import { NclPanel, NclSplitterPanel, NclView } from "../common/components.ncl";
import { GenerateControl } from "./K2GenerateControl";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";
import { K2Img } from "./K2Image";
import { Context } from "../appcontext";

type SplitterRuleProps = K2RuleWithVCXProps & {
  isVertical: boolean;
  ratio: number;
  mainPanel: boolean;
  hide?: boolean;
};

const SPLITTER_SIZE = 7;
const TOUCH_MARGIN = 20;
const ALLOWED_SWIPE_AREA = 200;

export const SplitterContext = React.createContext(null);

const splitter = (props: K2RuleWithVCXProps & { isVertical: boolean }) => ({
  gap: props.isVertical ? props.vcx.Data.MarginX + "px" : props.vcx.Data.MarginY + "px",
  small: {
    gap: props.isVertical ? 0 : props.vcx.Data.MarginY + "px",
  },
});

const splitterRule = (props: SplitterRuleProps) => ({
  backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck),
  width: props.isVertical ? `${props.vcx.sizeMap(SPLITTER_SIZE)}px` : "auto",
  height: props.isVertical ? "auto" : `${props.vcx.sizeMap(SPLITTER_SIZE)}px`,
  cursor: props.isVertical ? "col-resize" : "row-resize",
  flex: "0 0 auto",
  transition: `${props.isVertical ? "width" : "height"} 0.25s`,
  justifyContent: "center",
  alignItems: "center",
  visibility: props.hide ? ("hidden" as const) : ("visible" as const),
  small: {
    display: props.mainPanel ? "none" : "flex",
    backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.AccentBaseColorBck),
    position: props.isVertical ? ("absolute" as const) : null,
    height: props.isVertical ? `${props.vcx.sizeMap(50)}px` : null,
    width: props.isVertical ? `${props.vcx.sizeMap(20)}px` : null,
    top: props.isVertical ? "50%" : null,
    transform: props.isVertical ? "translate(0, -50%)" : null,
    right: 0,
    zIndex: 1,
  },
});

const dots = (props: SplitterRuleProps) => ({
  width: `${props.vcx.sizeMap(7)}px`,
  height: `${props.vcx.sizeMap(19)}px`,
  backgroundImage: `radial-gradient(circle, white ${props.vcx.sizeMap(1)}px, transparent ${props.vcx.sizeMap(2)}px)`,
  backgroundSize: "100% 33.33%",
  transform: !props.isVertical ? "rotate(90deg)" : null,
});

const mobile = (props: SplitterRuleProps) => ({
  display: "flex",
  small: {
    height: props.mainPanel ? `calc(100% - ${props.vcx.ExpanderControl.GetHFHeight() + props.vcx.sizeMap(6)}px)` : null,
  },
});

interface SplitterState extends K2ComponentState<UpdateSplitterPanel> {
  ratio: number;
  mobile: boolean;
}

class _SplitterPanel extends React.PureComponent<WithContextPlacementProps, SplitterState> {
  static displayName = "K2SplitterPanel";
  private control: NclSplitterPanel;
  private dragging: boolean;
  private timer: number;
  private currentTarget: HTMLDivElement;
  private rect: DOMRect;

  constructor(props: WithContextPlacementProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => ctrl instanceof NclSplitterPanel) as NclSplitterPanel;
    this.state = {
      data: this.control.init(this) as UpdateSplitterPanel,
      vcxVersion: -1,
      ratio: 0.75,
      mobile: window.innerWidth > Context.DeviceInfo.ResponsiveBreakpoints[0] ? false : true,
    };

    window.addEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0]) {
      this.setState({ ratio: Math.round(this.state.ratio), mobile: true });
    } else {
      this.setState({ ratio: this.state.ratio, mobile: false });
    }
  };

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.control.willUnMount(true);
    this.control = null;
  }

  updateState(state: UpdateSplitterPanel) {
    this.setState({
      data: state as UpdateSplitterPanel,
      ratio: this.isSplitterVertical() && window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0] ? Math.round(state.Ratio) : state.Ratio,
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  isSplitterVertical = () => {
    if (
      this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoLeft ||
      this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoRight
    )
      return true;
    else return false;
  };

  handleMouseDown = (e: React.MouseEvent) => {
    this.dragging = true;
    this.rect = e.currentTarget.parentElement.getBoundingClientRect();
  };

  handleContextMenu = (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    if (this.dragging) {
      e.stopPropagation();
    }
  };

  handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.currentTarget.children[1] === undefined) return;
    this.currentTarget = e.currentTarget;
    this.rect = e.currentTarget.getBoundingClientRect();
    const splitter = e.currentTarget.children[1].getBoundingClientRect();
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;

    // Vymezeni mista, kde je mozne vyvolat resize
    if (
      (this.isSplitterVertical() && clientX > splitter.left - TOUCH_MARGIN && clientX < splitter.right + TOUCH_MARGIN) ||
      (this.isSplitterVertical() === false && clientY > splitter.top - TOUCH_MARGIN && clientY < splitter.bottom + TOUCH_MARGIN)
    ) {
      e.stopPropagation();

      // Simulace long touche
      this.timer = window.setTimeout(() => {
        if (this.isSplitterVertical()) {
          this.dragStart(this.currentTarget.children[1] as HTMLDivElement, "width");
        } else if (this.isSplitterVertical() === false) {
          this.dragStart(this.currentTarget.children[1] as HTMLDivElement, "height");
        }
      }, 500);
    }
  };

  dragStart = (splitter: HTMLDivElement, dimension: string) => {
    this.dragging = true;
    splitter.style.cssText = `${dimension}: ${this.control.VCX.sizeMap(SPLITTER_SIZE * 2)}px;`;
  };

  dragCancel = (splitter: HTMLDivElement, dimension: string) => {
    clearTimeout(this.timer);
    this.dragging = false;
    splitter.style.cssText = `${dimension}: ${null}`;
  };

  handleMouseTouchMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!this.dragging) return;
    e.stopPropagation();

    // Podminka zajisti preruseni resizu na dotykovach zarizenich, pokud uzivatel zacne vyvolavat long touch, ale rychle swipne pryc z oblasti okolo splitteru
    if (
      window.TouchEvent &&
      e.nativeEvent instanceof TouchEvent &&
      this.isSplitterVertical() &&
      !(
        e.nativeEvent.touches[0].clientX > e.currentTarget.children[1].getBoundingClientRect().left - ALLOWED_SWIPE_AREA &&
        (e as React.TouchEvent).touches[0].clientX < e.currentTarget.children[1].getBoundingClientRect().right + ALLOWED_SWIPE_AREA
      )
    ) {
      this.dragCancel(e.currentTarget.children[1] as HTMLDivElement, "width");
      return;
    } else if (
      window.TouchEvent &&
      e.nativeEvent instanceof TouchEvent &&
      this.isSplitterVertical() === false &&
      !(
        e.nativeEvent.touches[0].clientY > e.currentTarget.children[1].getBoundingClientRect().top - ALLOWED_SWIPE_AREA &&
        (e as React.TouchEvent).touches[0].clientY < e.currentTarget.children[1].getBoundingClientRect().bottom + ALLOWED_SWIPE_AREA
      )
    ) {
      this.dragCancel(e.currentTarget.children[1] as HTMLDivElement, "height");
      return;
    }

    let clientX: number;
    let clientY: number;

    if (e.nativeEvent instanceof MouseEvent) {
      clientX = (e as React.MouseEvent<HTMLDivElement>).clientX;
      clientY = (e as React.MouseEvent<HTMLDivElement>).clientY;
    } else {
      clientX = (e as React.TouchEvent<HTMLDivElement>).touches[0].clientX;
      clientY = (e as React.TouchEvent<HTMLDivElement>).touches[0].clientY;
    }

    if (this.isSplitterVertical()) {
      const xPos = clientX - this.rect.left;
      const ratio = xPos / e.currentTarget.offsetWidth;
      this.setState({ ratio: ratio });
    } else {
      const yPos = clientY - this.rect.top;
      const ratio = yPos / e.currentTarget.offsetHeight;
      this.setState({ ratio: ratio });
    }
  };

  handleMouseUpTouchEnd = () => {
    if (this.timer) {
      if (this.isSplitterVertical()) {
        this.dragCancel(this.currentTarget.children[1] as HTMLDivElement, "width");
      } else {
        this.dragCancel(this.currentTarget.children[1] as HTMLDivElement, "height");
      }
    }
    this.dragging = false;
    this.control.setRatio(this.state.ratio);
  };

  handleMouseLeave = () => {
    this.dragging = false;
  };

  handleDoubleClick = () => {
    this.control.toggleCollapsed();
  };

  handleClick = () => {
    if (!this.isSplitterVertical()) return;
    if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0]) {
      this.setState({ ratio: this.state.ratio === 0 ? 1 : 0 });
    }
  };

  componentDidUpdate() {
    if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0] && this.state.data.Collapsed) {
      this.control.toggleCollapsed();
    }
  }

  private isCollapsed(first: boolean): boolean {
    if (this.state.data.Visible === false) return true;
    if (this.state.data.Collapsed) {
      if (
        (first &&
          (this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoRight ||
            this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoBottom)) ||
        (!first &&
          (this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoLeft ||
            this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoTop))
      ) {
        return false;
      } else {
        return true;
      }
    }

    return false;
  }

  isOnlyFirstVisible = () => {
    if (this.state.data.FirstVisible === false && this.state.data.SecondVisible === true) return true;
    else return false;
  };

  isOnlySecondVisible = () => {
    if (this.state.data.FirstVisible === true && this.state.data.SecondVisible === false) return true;
    else return false;
  };

  setStyle = (first: boolean) => {
    let style: React.CSSProperties = {};

    if (this.state.data.Collapsed) {
      if (
        (first &&
          (this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoRight ||
            this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoBottom)) ||
        (!first &&
          (this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoLeft ||
            this.control.Ncl.FrgtData.Orientation === SplitterPanelOrientation.spoTop))
      ) {
        style = { flexGrow: 1 };
      } else {
        style = { display: "none" };
      }
    } else {
      style = { flexGrow: 1 };
      if (this.isSplitterVertical()) {
        if (first) {
          if (this.control.Children.get(0) === undefined) {
            style = { ...style, flexGrow: 0 };
          } else {
            style = {
              ...style,
              width: this.state.ratio * 100 + "%",
              display: this.isOnlyFirstVisible() ? "none" : "flex",
            };
          }

          if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0]) {
            style = { ...style, transition: "width 0.5s ease-in-out 0s", padding: `${this.control.VCX.sizeMap(3)}px 0px` };
          }
        } else {
          if (this.control.Children.get(1) === undefined) {
            style = { ...style, flexGrow: 0 };
          } else {
            style = {
              ...style,
              width: (1 - this.state.ratio) * 100 + "%",
              display: this.isOnlySecondVisible() ? "none" : "flex",
            };
          }

          if (window.innerWidth < Context.DeviceInfo.ResponsiveBreakpoints[0]) {
            style = { ...style, transition: "width 0.5s ease-in-out 0s", padding: `${this.control.VCX.sizeMap(3)}px 0px` };
          }
        }
      } else {
        if (first) {
          if (this.control.Children.get(0) === undefined) {
            style = { ...style, flexGrow: 0 };
          } else {
            style = {
              ...style,
              height: this.state.ratio * 100 + "%",
              display: this.isOnlyFirstVisible() ? "none" : "flex",
              minHeight:
                this.control.Children.get(0) instanceof NclPanel && (this.control.Children.get(0).MetaData.FrgtData as FrgtPanelBaseData).Scroll === 1
                  ? this.control.Children.get(0).VCX.LabelControl.getHeight(this.control.Children.get(0).Size)
                  : this.control.Children.get(0).ComputedMinHeightWithMargin,
            };
          }
        } else {
          if (this.control.Children.get(1) === undefined) {
            style = { ...style, flexGrow: 0 };
          } else {
            style = {
              ...style,
              height: (1 - this.state.ratio) * 100 + "%",
              display: this.isOnlySecondVisible() ? "none" : "flex",
              minHeight:
                this.control.Children.get(1) instanceof NclPanel && (this.control.Children.get(1).MetaData.FrgtData as FrgtPanelBaseData).Scroll === 1
                  ? this.control.Children.get(1).VCX.LabelControl.getHeight(this.control.Children.get(1).Size)
                  : this.control.Children.get(1).ComputedMinHeightWithMargin,
            };
          }
        }
      }
      style = { overflow: "auto", ...style };
    }

    return style;
  };

  setRatio = () => {
    this.setState({ ratio: this.state.ratio === 1 ? 0 : 1 });
  };

  render() {
    return (
      <SplitterContext.Provider value={{ ratio: this.state.ratio, setRatio: this.setRatio }}>
        <div
          style={StyleHelper(this.control, {
            position: "relative",
            flexDirection: this.isSplitterVertical() ? "row" : "column",
            WebkitUserSelect: "none",
            ...this.props.style,
          })}
          className={this.props.renderer.renderRule(splitter, { vcx: this.control.VCX, isVertical: this.isSplitterVertical() })}
          onTouchStartCapture={this.handleTouchStart}
          onTouchMoveCapture={this.handleMouseTouchMove}
          onTouchEnd={this.handleMouseUpTouchEnd}
          onMouseMove={this.handleMouseTouchMove}
          onMouseUp={this.handleMouseUpTouchEnd}
          onMouseLeave={this.handleMouseLeave}
          onContextMenuCapture={this.handleContextMenu}
        >
          <div style={this.setStyle(true)}>{!this.isCollapsed(true) ? GenerateControl(this.control.Children.get(0), { alignSelf: "stretch" }) : null}</div>
          <div
            className={this.props.renderer.renderRule(splitterRule, {
              vcx: this.control.VCX,
              isVertical: this.isSplitterVertical(),
              ratio: this.state.ratio,
              mainPanel: this.control.Parent instanceof NclPanel && this.control.Parent.Parent instanceof NclView,
              hide: !this.state.data.Collapsed && (this.isOnlyFirstVisible() || this.isOnlySecondVisible()) ? true : false,
            })}
            onMouseDown={this.handleMouseDown}
            onDoubleClick={this.handleDoubleClick}
            onClick={this.handleClick}
          >
            {this.isSplitterVertical() && this.state.mobile ? (
              <K2Img
                vcx={this.control.VCX}
                glyphId={this.state.ratio === 0 ? "wui*triangleright" : "wui*triangleleft"}
                height={15}
                width={15}
                strokeColor="white"
              />
            ) : (
              <div
                className={this.props.renderer.renderRule(dots, {
                  vcx: this.control.VCX,
                  isVertical: this.isSplitterVertical(),
                  ratio: this.state.ratio,
                  mainPanel: this.control.Parent instanceof NclPanel && this.control.Parent.Parent instanceof NclView,
                })}
              ></div>
            )}
          </div>
          <div
            style={this.setStyle(false)}
            className={this.props.renderer.renderRule(mobile, {
              vcx: this.control.VCX,
              isVertical: this.isSplitterVertical(),
              ratio: this.state.ratio,
              mainPanel: this.control.Parent instanceof NclPanel && this.control.Parent.Parent instanceof NclView,
            })}
          >
            {!this.isCollapsed(false) ? GenerateControl(this.control.Children.get(1), { alignSelf: "stretch" }) : null}
          </div>
        </div>
      </SplitterContext.Provider>
    );
  }
}

export const K2SplitterPanel = withContext(_SplitterPanel);
